import type { SendsarClient } from "./client.js";
import type { TypingEvent } from "./types.js";

/** Pause before sending "stopped typing" (matches common chat apps). */
export const TYPING_STOP_MS = 2000;

/** Shown in 1:1 chats — no name needed. */
export const TYPING_DM_LABEL = "Typing…";

export type TypingByRoom = Record<string, Set<string>>;

export function applyTypingEvent(prev: TypingByRoom, event: TypingEvent): TypingByRoom {
  const next = { ...prev };
  const roomTyping = new Set(next[event.roomId] ?? []);
  if (event.isTyping) roomTyping.add(event.userId);
  else roomTyping.delete(event.userId);
  if (roomTyping.size === 0) delete next[event.roomId];
  else next[event.roomId] = roomTyping;
  return next;
}

export function clearUserTyping(
  prev: TypingByRoom,
  roomId: string,
  userId: string,
): TypingByRoom {
  const roomTyping = prev[roomId];
  if (!roomTyping?.has(userId)) return prev;
  const next = { ...prev };
  const set = new Set(next[roomId]);
  set.delete(userId);
  if (set.size === 0) delete next[roomId];
  else next[roomId] = set;
  return next;
}

/** Typing users in a room, excluding yourself. */
export function otherTypingUserIds(
  typingByRoom: TypingByRoom,
  roomId: string,
  selfUserId: string,
): string[] {
  return [...(typingByRoom[roomId] ?? [])].filter((id) => id !== selfUserId);
}

export type FormatTypingLabelOptions = {
  /** 1:1 chat — show "Typing…" without a name. */
  directMessage?: boolean;
};

/**
 * Standard typing label for inbox previews and thread indicator.
 * - DM: "Typing…"
 * - Group: "Alice is typing…" / "Alice and Bob are typing…"
 */
export function formatTypingLabel(
  typingUserIds: readonly string[],
  displayNames: Readonly<Record<string, string>>,
  options?: FormatTypingLabelOptions,
): string {
  if (typingUserIds.length === 0) return "";
  if (options?.directMessage) return TYPING_DM_LABEL;

  const names = typingUserIds.map((id) => displayNames[id] ?? "Someone");
  if (names.length === 1) return `${names[0]} is typing…`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} others are typing…`;
}

/** Subtitle for a 1:1 inbox row: typing beats last message preview. */
export function inboxSubtitleForPeer(options: {
  typingLabel?: string;
  lastMessagePreview?: string;
}): string | undefined {
  if (options.typingLabel) return options.typingLabel;
  return options.lastMessagePreview;
}

type ComposerTypingClient = Pick<SendsarClient, "isConnected" | "setTyping">;

/** Debounced outgoing typing indicator while the user composes a message. */
export class ComposerTypingController {
  private readonly client: ComposerTypingClient;
  private roomId: string | undefined;
  private readonly stopMs: number;
  private typingActive = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(client: ComposerTypingClient, roomId: string | undefined, stopMs = TYPING_STOP_MS) {
    this.client = client;
    this.roomId = roomId;
    this.stopMs = stopMs;
  }

  setRoomId(roomId: string | undefined) {
    if (this.roomId === roomId) return;
    this.stop();
    this.roomId = roomId;
  }

  onValueChange(value: string) {
    if (!this.roomId || !this.client.isConnected) return;
    const hasText = value.trim().length > 0;

    if (hasText) {
      if (!this.typingActive) {
        this.typingActive = true;
        this.client.setTyping({ roomId: this.roomId, isTyping: true });
      }
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => this.stop(), this.stopMs);
    } else {
      this.stop();
    }
  }

  stop() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.typingActive && this.roomId && this.client.isConnected) {
      this.client.setTyping({ roomId: this.roomId, isTyping: false });
      this.typingActive = false;
    }
  }

  destroy() {
    this.stop();
  }
}
